import React, { useState } from 'react';
import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';
import * as GiIcons from 'react-icons/gi';
import * as BiIcons from 'react-icons/bi';
import { useTranslation } from '../../context/LanguageContext';

const IconPicker = ({ value, onChange, error }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('food');

    // Icon categories
    const categories = {
        food: [
            { icon: GiIcons.GiBowlOfRice, name: 'Rice Bowl' },
            { icon: GiIcons.GiSlicedBread, name: 'Bread' },
            { icon: GiIcons.GiNoodles, name: 'Noodles' },
            { icon: GiIcons.GiTacos, name: 'Tacos' },
            { icon: GiIcons.GiFruitBowl, name: 'Fruit Bowl' },
            { icon: GiIcons.GiCupcake, name: 'Cupcake' },
            { icon: GiIcons.GiMeat, name: 'Meat' },
            { icon: GiIcons.GiFrenchFries, name: 'French Fries' },
            { icon: GiIcons.GiChickenLeg, name: 'Chicken' },
            { icon: GiIcons.GiSushis, name: 'Sushi' },
            { icon: GiIcons.GiHotMeal, name: 'Hot Meal' },
            { icon: GiIcons.GiSandwich, name: 'Sandwich' },
            { icon: GiIcons.GiSaltShaker, name: 'Salt' },
            { icon: GiIcons.GiFoodChain, name: 'Food Chain' },
        ],
        drinks: [
            { icon: GiIcons.GiCoffeeCup, name: 'Coffee' },
            { icon: GiIcons.GiTeapot, name: 'Tea' },
            { icon: GiIcons.GiSodaCan, name: 'Soda' },
            { icon: GiIcons.GiWaterBottle, name: 'Water' },
            { icon: GiIcons.GiMilkCarton, name: 'Milk' },
            { icon: GiIcons.GiWineGlass, name: 'Wine' },
            { icon: GiIcons.GiBeerStein, name: 'Beer' },
            { icon: GiIcons.GiGlassShot, name: 'Shot' },
        ],
        desserts: [
            { icon: GiIcons.GiIceCreamCone, name: 'Ice Cream' },
            { icon: GiIcons.GiCakeSlice, name: 'Cake' },
            { icon: FaIcons.FaCookie, name: 'Cookie' },
            { icon: GiIcons.GiChocolateBar, name: 'Chocolate' },
            { icon: GiIcons.GiHoneycomb, name: 'Honey' },
        ],
        other: [
            { icon: BiIcons.BiRestaurant, name: 'Restaurant' },
            { icon: MdIcons.MdRestaurantMenu, name: 'Menu' },
            { icon: MdIcons.MdFoodBank, name: 'Food Bank' },
            { icon: MdIcons.MdOutlineEmojiFoodBeverage, name: 'Food Beverage' },
            { icon: FaIcons.FaUtensils, name: 'Utensils' },
            { icon: FaIcons.FaConciergeBell, name: 'Service Bell' },
            { icon: FaIcons.FaHamburger, name: 'Hamburger' },
            { icon: FaIcons.FaPizzaSlice, name: 'Pizza' },
        ],
    };

    const filteredIcons = searchTerm
        ? Object.values(categories)
            .flat()
            .filter(item => 
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        : categories[selectedCategory];

    const handleSelect = (iconName) => {
        onChange(iconName);
        setIsOpen(false);
    };

    const handleCategoryClick = (e, category) => {
        e.preventDefault(); // Prevent form submission
        e.stopPropagation(); // Stop event bubbling
        setSelectedCategory(category);
    };

    return (
        <div className="relative">
            <label className="admin-label">{t('admin:iconPicker.label')}</label>
            <div 
                className="flex items-center space-x-2 p-2 border rounded-md cursor-pointer hover:border-yellow-500"
                onClick={() => setIsOpen(!isOpen)}
            >
                {value ? (
                    <>
                        {React.createElement(
                            Object.values(categories)
                                .flat()
                                .find(item => item.name === value)?.icon || FaIcons.FaQuestionCircle,
                            { className: "w-6 h-6" }
                        )}
                        <span>{value}</span>
                    </>
                ) : (
                    <span className="text-gray-500">{t('admin:iconPicker.selectIcon')}</span>
                )}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

            {isOpen && (
                <div className="absolute z-50 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200" onClick={e => e.stopPropagation()}>
                    <div className="p-4">
                        <input
                            type="text"
                            placeholder={t('admin:iconPicker.searchPlaceholder')}
                            className="w-full px-3 py-2 border rounded-md mb-3"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                        />

                        {!searchTerm && (
                            <div className="flex space-x-2 mb-3 flex-wrap gap-2">
                                {Object.keys(categories).map((category) => (
                                    <button
                                        key={category}
                                        type="button"
                                        onClick={(e) => handleCategoryClick(e, category)}
                                        className={`px-3 py-1 rounded-full text-sm ${
                                            selectedCategory === category
                                                ? 'bg-yellow-500 text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {t(`admin:iconPicker.categories.${category}`)}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                            {filteredIcons.map(({ icon: Icon, name }) => (
                                <div
                                    key={name}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSelect(name);
                                    }}
                                    className={`p-2 flex flex-col items-center justify-center rounded-md cursor-pointer hover:bg-yellow-50 ${
                                        value === name ? 'bg-yellow-100' : ''
                                    }`}
                                >
                                    <Icon className="w-6 h-6" />
                                    <span className="text-xs mt-1 text-center">{t(`admin:iconPicker.icons.${name.toLowerCase().replace(/\s+/g, '_')}`)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IconPicker;
